import {FILE_REJECTED_TRANSLATIONS} from '@hconnect/common/consts'
import {imageBoxSx, clickableSx} from '@hconnect/common/styles'
import {AttachmentFile} from '@hconnect/common/types'
import {LoadingSwitcher} from '@hconnect/uikit/src/lib2'
import {spawnInfoToast, spawnWarningToast} from '@hm/fundamental'
import {Add, Cancel} from '@mui/icons-material'
import {Box, Divider, IconButton, SxProps, Theme} from '@mui/material'
import {isEmpty} from 'lodash'
import {nanoid} from 'nanoid'
import React, {useCallback, useRef} from 'react'
import {useTranslation} from 'react-i18next'

import {COMMON_ATTACHMENTS_WIDGET_NAMESPACE} from '../consts'
import {type FileUploadEvent, FileUploadEventType, type AttachementUploadConfig} from '../types'

import {AttachmentHandler, imageFluidSx} from './AttachmentHandler'

type DeletedFiles = Record<string, AttachmentFile>

export const AttachmentUpload: React.FC<{
  attachments: AttachmentFile[]
  setAttachments: (attachments: AttachmentFile[]) => void
  config: AttachementUploadConfig
  attachmentsTitle?: JSX.Element
  containerSx?: SxProps<Theme>
}> = ({attachments, setAttachments, config, attachmentsTitle, containerSx}) => {
  const {t} = useTranslation(COMMON_ATTACHMENTS_WIDGET_NAMESPACE)
  const deletedFilesForUndo = useRef<DeletedFiles>({})
  const loading = !Array.isArray(attachments)

  const {
    allowedFormats,
    maxAttachements,
    maxFileSizeMB,
    isAddingEnabled = false,
    isDeletingEnabled = false
  } = config

  const notifyUploadEvent = useCallback(
    (event: FileUploadEvent) => {
      switch (event.type) {
        case FileUploadEventType.FILE_DUPLICATED:
          return spawnInfoToast(`${t('attachmentUpload.message.duplicateFiles')} ${event.fileName}`)
        case FileUploadEventType.FILE_SIZE_TOO_LARGE:
          return spawnInfoToast(`${t(FILE_REJECTED_TRANSLATIONS[1])} ${event.fileName}`)
        case FileUploadEventType.FILE_TYPE_INVALID:
          return spawnInfoToast(`${t(FILE_REJECTED_TRANSLATIONS[0])} ${event.fileName}`)
        case FileUploadEventType.FILE_LIMIT_REACHED:
          return spawnInfoToast(
            `${t('attachmentUpload.message.fileLimitReached')} ${event.fileName}`
          )
        case FileUploadEventType.FILE_REMOVED:
          return spawnWarningToast(
            <>
              {t('attachmentUpload.message.attachmentDeleted')} &nbsp;{' '}
              {t('attachmentUpload.action.undo')}
            </>,
            {onClose: event.undoRemove}
          )
      }
    },
    [t]
  )

  const onFileChange = useCallback(
    (newAttachments: FileList) => {
      if (attachments.length <= maxAttachements) {
        const attachmentsToAppend: AttachmentFile[] = []
        Array.from(newAttachments).forEach((attachment) => {
          if (!allowedFormats.some((format) => attachment.name.toLowerCase().includes(format))) {
            return notifyUploadEvent({
              type: FileUploadEventType.FILE_TYPE_INVALID,
              fileName: attachment.name
            })
          }
          // converting MB to binary Bytes
          if (attachment.size > maxFileSizeMB * 1024 * 1024) {
            return notifyUploadEvent({
              type: FileUploadEventType.FILE_SIZE_TOO_LARGE,
              fileName: attachment.name
            })
          }
          const attachmentToAppend: AttachmentFile = {
            name: attachment.name,
            contentType: attachment.type,
            preview: URL.createObjectURL(attachment),
            isImage: attachment.type.includes('image'),
            originalFile: attachment
          }

          attachmentsToAppend.push(attachmentToAppend)
        })

        const duplicateFiles = attachmentsToAppend.filter((a) =>
          attachments.some((b) => b.name === a.name)
        )

        const fileCountExceededFiles = attachmentsToAppend
          .filter((a) => !duplicateFiles.includes(a))
          .slice(maxAttachements - attachments.length)

        if (duplicateFiles.length > 0) {
          duplicateFiles.forEach((filtered) => {
            notifyUploadEvent({
              type: FileUploadEventType.FILE_DUPLICATED,
              fileName: filtered.name
            })
          })
        }

        if (fileCountExceededFiles.length > 0) {
          fileCountExceededFiles.forEach((exceeded) => {
            notifyUploadEvent({
              type: FileUploadEventType.FILE_LIMIT_REACHED,
              fileName: exceeded.name
            })
          })
        }

        const merge = attachments.concat(
          attachmentsToAppend
            .filter((a) => !attachments.some((b) => b.name === a.name))
            .slice(0, maxAttachements - attachments.length)
        )

        setAttachments(merge)
      }
    },
    [attachments, setAttachments, allowedFormats, maxAttachements, maxFileSizeMB, notifyUploadEvent]
  )

  const undoRemoveAttachment = useCallback(
    (key: string, prevAttachments: AttachmentFile[]) => {
      const fileToRecover = deletedFilesForUndo.current[key]
      if (!fileToRecover) return
      const isMaxAttachmentsExceeded = prevAttachments.length >= maxAttachements
      setAttachments(
        isMaxAttachmentsExceeded ? prevAttachments : [...prevAttachments, fileToRecover]
      )
    },
    [setAttachments, maxAttachements]
  )

  const removeAttachment = useCallback(
    (index: number) => {
      const key = nanoid()
      const copy = [...attachments]
      const [deletedFile] = copy.splice(index, 1)
      setAttachments(copy)
      deletedFilesForUndo.current[key] = deletedFile
      const undoRemove = () => {
        undoRemoveAttachment(key, copy)
      }
      notifyUploadEvent?.({
        type: FileUploadEventType.FILE_REMOVED,
        fileName: deletedFile.name,
        undoRemove
      })
    },
    [attachments, setAttachments, undoRemoveAttachment, notifyUploadEvent]
  )

  return (
    <LoadingSwitcher isLoading={loading}>
      <div>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            ...(containerSx ?? {})
          }}
        >
          {attachmentsTitle}
          {attachments.length < maxAttachements && isAddingEnabled && (
            <>
              <IconButton data-test-id="add-attachment-button">
                <Box
                  component="label"
                  display="flex"
                  htmlFor="attachmentInput"
                  data-test-id="add-image-placeholder"
                >
                  <Add sx={{color: 'primary.main', cursor: 'pointer'}} />
                </Box>
              </IconButton>
              <Box
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  if (e.target.files) {
                    onFileChange(e.target.files)
                    e.target.value = ''
                  }
                }}
                component="input"
                type="file"
                multiple={true}
                accept={allowedFormats.join(', ')}
                id="attachmentInput"
                display="none"
              />
            </>
          )}
        </Box>
        {!isEmpty(attachments) && <Divider sx={{borderColor: 'common.black'}} />}
        <Box component="span" sx={imageBoxSx()}>
          {attachments.map((file, index) => (
            <span key={index} data-test-id={`upload-file-section-img-${index}`}>
              {isDeletingEnabled && (
                <Box
                  component={'span'}
                  sx={(theme) => ({
                    position: 'relative',
                    top: -10,
                    right: 10,
                    float: 'right',
                    width: 0,
                    height: 0,
                    '& svg': {
                      color: theme.palette.primary.main,
                      backgroundColor: theme.palette.background.paper,
                      borderRadius: '12px'
                    },
                    ...clickableSx()
                  })}
                >
                  <Cancel
                    data-test-id={`delete-image-${index}`}
                    onClick={() => removeAttachment(index)}
                  />
                </Box>
              )}
              <AttachmentHandler
                key={file.id}
                file={file.preview}
                alt={file.name}
                iconOrPreview={file.isImage ? 'preview' : 'icon'}
                mediaType={file.contentType}
                sx={imageFluidSx}
              />
            </span>
          ))}
        </Box>
      </div>
    </LoadingSwitcher>
  )
}
